import { STRAPI_CONFIG_KEY } from '@thyme/libs/src/strapi/config'
import { listPublishedArticlesWithUseQuery } from '@thyme/libs/src/strapi/queries'
import { ArticleResponse } from '@thyme/libs/src/strapi/types'
import { storeToRefs } from 'pinia'
import {
  computed,
  ComputedRef,
  ExtractPropTypes,
  inject,
  onMounted,
  ref,
} from 'vue'
import { CareTeamDocument } from '@/api/catchAll/careTeamDocuments/types'
import { useConfigStore } from '@/legacy/store/modules/config'
import { useCareTeamDocumentApi } from '@/pages/Tools/SopLibrary/store'
import { useArticleLinks } from '../connectArticles/articleLinks'
import {
  ArticlesWithDocumentsByCategory,
  CONNECT_ARTICLES_PROPS,
} from './types'

const CONNECT_ARTICLE = 'CONNECT_ARTICLE'
const SHOW_CHECKMARK_DURATION_MS = 1500

type ConnectArticlesProps = ExtractPropTypes<typeof CONNECT_ARTICLES_PROPS>

/**
 * Setup function for ConnectArticles
 * @param props
 */
export function setup(props: ConnectArticlesProps) {
  const { configVals } = storeToRefs(useConfigStore())

  const strapiConfig = inject(STRAPI_CONFIG_KEY)
  if (!strapiConfig) {
    throw new Error('Strapi config not provided')
  }

  const externalThymeboxPatientId = computed(
    () => props.externalThymeboxPatientId
  )
  const { data: articles, isLoading } =
    listPublishedArticlesWithUseQuery(strapiConfig)
  const { data: connectCareTeamDocuments } = storeToRefs(
    useCareTeamDocumentApi()
  )

  const getCategory = (article: ArticleResponse): string | null =>
    article.resource_category?.name ?? null

  const sortKey = (article: ArticleResponse) =>
    article.title.toLowerCase() ?? ''

  // Get slug from CareTeamDocument fileName and strip .md extension
  const slugFromDocument = (document: CareTeamDocument) =>
    document.fileName?.replace(/\.md$/, '')

  // Map slugs to CareTeamDocuments
  const slugsToDocuments: ComputedRef<Record<string, CareTeamDocument>> =
    computed(() =>
      Object.fromEntries(
        (connectCareTeamDocuments.value ?? [])
          .map((doc) => [slugFromDocument(doc), doc])
          .filter(([slug]) => Boolean(slug))
      )
    )

  /**
   * Sorts articles in place by their  title in alphabetical order
   * @param items - The array of ArticleResponse objects to sort
   */
  function sortInPlace(items: ArticleResponse[]) {
    items.sort((a, b) => sortKey(a).localeCompare(sortKey(b)))
  }

  // Map alphabetized categories to alphabetized articles
  const sortedArticlesByCategory: ComputedRef<
    Record<string, ArticleResponse[]>
  > = computed(() => {
    const articlesByCategory: Record<string, ArticleResponse[]> = {}
    const strapiArticles = articles.value ?? []

    // Group articles by category
    for (const article of strapiArticles) {
      const category = getCategory(article)
      if (category) {
        if (!articlesByCategory[category]) {
          articlesByCategory[category] = []
        }
        articlesByCategory[category].push(article)
      }
    }

    // Sort the articles in alphabetical order within each category
    for (const articles of Object.values(articlesByCategory)) {
      sortInPlace(articles)
    }

    // Sort the categories
    const sortedResult: Record<string, ArticleResponse[]> = {}
    const sortedCategories = Object.keys(articlesByCategory).sort((a, b) =>
      a.localeCompare(b)
    )

    for (const category of sortedCategories) {
      sortedResult[category] = articlesByCategory[category]
    }

    return sortedResult
  })

  const articlesWithDocumentsByCategory: ComputedRef<ArticlesWithDocumentsByCategory> =
    computed(() => {
      const result: ArticlesWithDocumentsByCategory = {}

      for (const [category, articlesArr] of Object.entries(
        sortedArticlesByCategory.value
      )) {
        const slugToArticleData: Record<
          string,
          { document: CareTeamDocument; strapiArticleTitle: string }
        > = {}

        for (const article of articlesArr) {
          const slug = article.slug
          const document = slugsToDocuments.value[slug]
          if (document !== undefined) {
            slugToArticleData[slug] = {
              document,
              strapiArticleTitle: article.title,
            }
          }
        }

        if (Object.keys(slugToArticleData).length > 0) {
          result[category] = slugToArticleData
        }
      }

      return result
    })

  onMounted(async () => {
    await useCareTeamDocumentApi().listAll({
      params: {
        filter_care_team_document_categories: [CONNECT_ARTICLE],
      },
    })
  })

  const connectUrl = configVals.value?.connect.url

  const isLibraryLinkCopied = ref(false)
  const copyLibraryLink = async () => {
    if (connectUrl) {
      await articleLink.copyLink(connectUrl + '/library')
      isLibraryLinkCopied.value = true
      setTimeout(() => {
        isLibraryLinkCopied.value = false
      }, SHOW_CHECKMARK_DURATION_MS)
    }
  }

  /**
   * Finds a CareTeamDocument by its slug.
   * @param slug - The slug to search for.
   * @returns The matching CareTeamDocument, or undefined if not found.
   */
  function findDocumentBySlug(slug: string): CareTeamDocument | undefined {
    const documents = connectCareTeamDocuments.value ?? []
    return documents.find((doc) => slugFromDocument(doc) === slug)
  }

  const copiedBySlug = ref<Record<string, boolean>>({})

  const articleLink = useArticleLinks(externalThymeboxPatientId)
  const meiliSearchCopyLink = async (slug: string) => {
    const matchingDoc = findDocumentBySlug(slug)
    if (matchingDoc?.sourceUrl) {
      await articleLink.copyLink(matchingDoc.sourceUrl)
      copiedBySlug.value[slug] = true
      setTimeout(() => {
        copiedBySlug.value[slug] = false
      }, SHOW_CHECKMARK_DURATION_MS)
    }
  }

  const meiliSearchViewArticle = (slug: string) => {
    const matchingDoc = findDocumentBySlug(slug)
    if (matchingDoc?.careTeamDocumentId) {
      articleLink.viewArticle(matchingDoc.careTeamDocumentId)
    }
  }

  return {
    isLoading,
    articlesWithDocumentsByCategory,
    meiliSearchCopyLink,
    meiliSearchViewArticle,
    copiedBySlug,
    copyLibraryLink,
    isLibraryLinkCopied,
  }
}
